import { autocompleteClasses } from "@mui/material/Autocomplete";
import { isNil } from "lodash";
import { isValidElement, MutableRefObject, ReactNode, Ref } from "react";
import { ListImperativeAPI } from "react-window";

import { buildPartsLayoutSignature } from "@ryde/shared";

import {
  DEFAULT_ROW_HEIGHT,
  MAX_LISTBOX_ITEMS_NUMBER,
} from "./list-box.constants";
import { IGetListViewportHeightParams } from "./list-box.models";

export const mergeRefs =
  <T>(...refs: (Ref<T> | undefined)[]) =>
  (node: T | null): void => {
    refs.forEach(refValue => {
      if (typeof refValue === "function") {
        refValue(node);
      } else if (refValue) {
        (refValue as MutableRefObject<T | null>).current = node;
      }
    });
  };

export const resolveEstimatedItemSize = (size: number | undefined): number =>
  typeof size === "number" && size > 0 ? size : DEFAULT_ROW_HEIGHT;

/** Measure MUI option row; prefer `<li>` so parent `style.height` does not clip. */
export const measureVirtualRowHeight = (container: HTMLElement): number => {
  const optionElement = container.querySelector("li");
  const target =
    optionElement instanceof HTMLElement ? optionElement : container;
  const height = target.offsetHeight;

  return height > 0 ? height : 0;
};

/** Changes when Autocomplete filter replaces options (not only when length changes). */
export const buildListLayoutSignature = (itemData: ReactNode[]): string => {
  const parts = itemData.map(item => {
    if (!isValidElement(item)) {
      return String(item);
    }

    const props = item.props as Record<string, unknown>;
    return [item.key, props["data-option-index"], props.id].join(":");
  });

  return buildPartsLayoutSignature(parts);
};

export const getVirtualListItemKey = (
  index: number,
  itemData: ReactNode[],
): string | number => {
  const item = itemData[index];
  if (isValidElement(item) && !isNil(item.key)) {
    return item.key;
  }

  return index;
};

export const getHighlightedOptionIndex = (
  outerElement: HTMLElement,
): number | undefined => {
  const highlightedOption = outerElement.querySelector<HTMLElement>(
    `li.${autocompleteClasses.option}.${autocompleteClasses.focused}`,
  );
  if (!highlightedOption) {
    return undefined;
  }

  const optionIndex = highlightedOption.getAttribute("data-option-index");
  if (optionIndex === null) {
    return undefined;
  }

  const index = Number(optionIndex);
  if (Number.isNaN(index)) {
    return undefined;
  }

  return index;
};

export const isHighlightedOptionVisible = (
  outerElement: HTMLElement,
): boolean => {
  const highlightedOption = outerElement.querySelector<HTMLElement>(
    `li.${autocompleteClasses.option}.${autocompleteClasses.focused}`,
  );
  if (!highlightedOption) {
    return true;
  }

  const containerRect = outerElement.getBoundingClientRect();
  const optionRect = highlightedOption.getBoundingClientRect();

  return (
    optionRect.top >= containerRect.top &&
    optionRect.bottom <= containerRect.bottom
  );
};

export const scrollVirtualListToHighlightedOption = (
  listInstance: ListImperativeAPI,
  outerElement: HTMLElement,
): void => {
  const index = getHighlightedOptionIndex(outerElement);
  if (index === undefined) {
    return;
  }

  if (isHighlightedOptionVisible(outerElement)) {
    return;
  }

  // MUI 9.0.1 sets scrollTop via offsetTop, which breaks react-window positioning.
  listInstance.scrollToRow({ index, align: "smart" });
};

export const getListViewportHeight = ({
  itemCount,
  cellSize,
  dynamicHeight,
  dynamicHeightRef,
}: IGetListViewportHeightParams): number => {
  if (dynamicHeight && dynamicHeightRef?.current) {
    return dynamicHeightRef.current.offsetHeight;
  }

  // Cap visible viewport; full list height still comes from virtualized scroll height.
  if (itemCount > MAX_LISTBOX_ITEMS_NUMBER) {
    return MAX_LISTBOX_ITEMS_NUMBER * cellSize;
  }

  return itemCount * cellSize;
};
